/**
 * VERA-291 follow-up — design-system consolidation, split by context.
 *
 * This file used to be ONE set of spacing values serving two genuinely
 * different contexts: mobile bottom-sheet pickers (SelectDrawer, the
 * profile gender/country lists, UpdatePhoto, and the mobile-drawer branch
 * of MoreOptionsMenu / ActionMenu) and desktop floating dropdowns
 * (SortField — the reference — and the desktop-menu branch of
 * MoreOptionsMenu / ActionMenu). Sharing one set of sizes meant the values
 * were always tuned for one context at the expense of the other — they
 * were desktop-menu sizes (15px font, from SortField) applied to phone tap
 * targets that landed under Apple's 44pt minimum touch target.
 *
 * The DROPDOWN half now composes `@repo/ui`'s house menu chrome
 * (`MENU_PAPER_SX` / `MENU_ITEM_SX`) rather than carrying its own numbers —
 * see `getPickerMenuProps()` at the bottom. A floating dropdown is the same
 * object in both apps, so its padding belongs to layer 1 (working-conventions
 * §47) and this file's job is the app-specific chrome around it (border,
 * shadow, brand-tinted hover) plus the sheet-vs-dropdown row-sizing split.
 * `PICKER_SHEET` is untouched: a bottom sheet is NOT a popover menu and does
 * not want a paper inset.
 *
 * Now there are two variants, `PICKER_SHEET` and `PICKER_DROPDOWN`, plus a
 * handful of legacy aliases (still exported, still pointed at
 * `PICKER_DROPDOWN`) so existing desktop call sites don't have to change.
 * Change a size HERE, not per-menu/per-drawer — the whole point of this
 * file is that the two families can't drift apart, or drift into each
 * other, again.
 */

import { MENU_ITEM_SX, MENU_PAPER_SX } from '@repo/ui'

import { menuRowToneSx } from './menuRowTone'

// The wrapping List's own outer px & py, for the SHEET lists (`SelectDrawer`,
// `EditFieldModal`). The dropdown family no longer reads it — a popover menu's
// inset is MENU_PAPER_SX's business now (4px paper + 4px list, same 8px total).
export const PICKER_LIST_PADDING = 1 // 8px

// Shared by both variants — matches theme.shape.borderRadius.
export const PICKER_ITEM_RADIUS = 8

/**
 * Bottom-sheet picker rows — primary TAP targets on a phone-first app
 * (reached by tapping an NFC tag with a phone). Consumers: `SelectDrawer`
 * (country/dial-code), `EditFieldModal`'s gender/country lists,
 * `UpdatePhoto`, and the MOBILE branch of `MoreOptionsMenu` / `ActionMenu`
 * (both of which render a desktop menu AND a mobile drawer from one
 * component — only the drawer branch reads this).
 *
 * Row height = itemPY*2 (px, theme spacing is MUI's 8px default in this
 * app — see working-conventions §29) + max(iconSize, lineHeight):
 *   12*2 + max(24, 24) = 48px — comfortably clears the 44px minimum,
 * even for a row with no leading icon (gender list): 12*2 + 24 = 48px too,
 * because `lineHeight` is set explicitly rather than left to inherit
 * body1's 20px.
 */
export const PICKER_SHEET = {
  fontSize: '16px',
  lineHeight: '24px',
  iconSize: 24,
  itemPY: 1.5, // 12px
  itemPX: 1.5, // 12px
  // A touch more separation between rows than the dense dropdown gap below —
  // these are primary tap targets, not a scan-and-click desktop list.
  itemGap: '4px',
  radius: PICKER_ITEM_RADIUS,
} as const

/**
 * Desktop floating-dropdown rows (an MUI `Select` / `Menu`'s item shape, as
 * opposed to `PickerListItem`'s bottom-sheet shape). Consumed by
 * `SortField` (the reference) and the DESKTOP branch of `MoreOptionsMenu` /
 * `ActionMenu`. UNCHANGED from the pre-split values — do not edit without
 * re-checking every desktop menu in the app; a desktop dropdown's
 * appearance must not move.
 */
export const PICKER_DROPDOWN = {
  fontSize: '15px',
  itemPY: 1.5, // 12px
  itemPX: 1.5, // 12px
  itemGap: '2px',
  radius: PICKER_ITEM_RADIUS,
} as const

// --- Legacy aliases -------------------------------------------------------
// Pre-split call sites imported these generic names directly. Kept pointed
// at PICKER_DROPDOWN (the historical values) so desktop consumers that
// haven't been migrated to read PICKER_DROPDOWN explicitly see zero change.
// New / touched consumers should prefer PICKER_SHEET / PICKER_DROPDOWN by
// name so the call site self-documents which context it's in.
export const PICKER_ITEM_PY = PICKER_DROPDOWN.itemPY
export const PICKER_ITEM_PX = PICKER_DROPDOWN.itemPX
export const PICKER_ITEM_GAP = PICKER_DROPDOWN.itemGap
export const PICKER_ITEM_FONT_SIZE = PICKER_DROPDOWN.fontSize
// CSS shorthand form of PICKER_DROPDOWN.itemPY/itemPX, for components whose
// API takes a `padding` string rather than an sx object (e.g. Menu.tsx).
export const PICKER_ITEM_PADDING = `${PICKER_DROPDOWN.itemPY * 8}px ${PICKER_DROPDOWN.itemPX * 8}px` // '12px 12px'

/**
 * Shared `MenuProps` for an MUI `Select` / `Menu` — the floating-dropdown
 * picker shape (as opposed to `PickerListItem`, which is the bottom-sheet
 * shape). Consumed by `SortField` (the reference) and `MoreOptionsMenu`'s
 * desktop menu so both stay on one definition instead of drifting. Always
 * PICKER_DROPDOWN — there is no sheet variant of an MUI floating menu.
 */
export const getPickerMenuProps = () => ({
  PaperProps: {
    sx: (theme: any) => ({
      // House menu chrome from `@repo/ui` — the SAME constants the platform's
      // ActionDropdown / Select / ActivationTriggerSelect spread, so an app dropdown
      // and a platform dropdown are one component to the eye rather than two families
      // that happen to be near each other. This is the app's single source for
      // floating-dropdown chrome (SortField, MoreOptionsMenu's desktop branch), so
      // fixing it here is what makes those two right — do NOT re-state the values at a
      // call site.
      //
      // MENU_PAPER_SX also replaces the hand-written `& .MuiList-root` inset that used
      // to live here: it carries `& .MuiMenu-list, & .MuiList-root { padding: 4px }`,
      // and a second rule for the same element would have won by declaration order and
      // silently kept the old 8px. Note the split changes but the TOTAL does not —
      // 0 paper + 8 list becomes 4 + 4.
      // The trigger→menu gap comes from MENU_PAPER_SX too (8px, absolute). It used to be
      // restated here as `mt: 1`, which happened to also be 8px on this app's MUI-default
      // spacing base — the same line rendered 4px in the platform. Two mechanisms agreeing
      // by coincidence is what the shared constant is for; there is now one.
      ...MENU_PAPER_SX,
      borderRadius: '12px',
      border: '1px solid #EDE9F6',
      boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
      backgroundColor: '#ffffff',
      '& .MuiMenuItem-root': {
        // 12px on all four sides, the nested-radius highlight, and the 4px gap between
        // rows. The padding was already 12 (PICKER_DROPDOWN.itemPX/PY resolve to 12px
        // on this app's 8px spacing base); the radius (8) and the row gap (2px) are what
        // drifted from the house 6 / 4px.
        ...MENU_ITEM_SX,
        mx: 0,
        fontSize: PICKER_DROPDOWN.fontSize,
        /*
          The app's one menu-row tone (`menuRowTone.ts`) — muted at rest, dark when
          selected, accent + `grey[50]` on hover.

          What this replaces: `color: '#000000'` at rest and a hardcoded
          `rgba(127,72,223,…)` wash — VERA PURPLE — on hover and selection. Three
          things wrong with it. It was the only menu in the app tinted with a
          brand colour rather than a neutral, so this dropdown did not match the
          one beside it. The purple is VERA's, not the tenant's, and this app
          renders inside a brand's own site, where asserting a colour nobody chose
          is the thing the whole app avoids (the black primary, the neutral field
          focus). And `SortField.tsx`'s own comment above its call to this
          function claims the treatment is "neutral (no brand/purple tint)" — the
          comment described the intent and the code had drifted from it.

          `&.Mui-selected` is spread separately from the base because MUI's own
          `Mui-selected` rule would otherwise win on specificity and repaint the
          background it just set.
        */
        ...menuRowToneSx(theme),
        '&.Mui-selected': {
          ...menuRowToneSx(theme, { selected: true, selectedBackground: true }),
        },
      },
    }),
  },
})
